import React, { useContext, useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { Spinner, Card, Text, Banner, BlockStack, Button } from '@shopify/polaris';
import { jwtDecode } from 'jwt-decode';

const API = process.env.REACT_APP_API_URL || 'https://api.baobrain.com';

// Helper to test expiry
function tokenIsExpired(token) {
  if (!token) return true;
  try {
    const decoded = jwtDecode(token);
    // JWT exp is in seconds, Date.now() is in milliseconds
    return Date.now() >= decoded.exp * 1000;
  } catch (e) {
    // If decoding fails (e.g., malformed token), treat as expired/invalid
    console.warn("Error decoding JWT:", e);
    return true;
  }
}

export default function LinkAccount({ platform: propPlatform }) { // Receive platform as a prop
  const { token, setToken, login } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [claimConflict, setClaimConflict] = useState(false);
  const [error, setError] = useState(null);
  const [hasHandledJwt, setHasHandledJwt] = useState(false);
  const [hasClaimed, setHasClaimed] = useState(false); // New state to prevent double-claiming

  // Pull query params
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const site_id = params.get('site_id');
  const site_token = params.get('site_token');
  // Use propPlatform if available, otherwise try to get from URL (e.g., for direct linking)
  const platform = propPlatform || params.get('platform') || 'BigCommerce'; // Default to 'BigCommerce' if not specified
  const store_hash = params.get('store_hash');
  const jwt_from_url = params.get('jwt');

  // Current path and query for return_to if user needs to log in
  const currentPathAndQuery = useMemo(() => `${location.pathname}${location.search}`, [location.pathname, location.search]);

  useEffect(() => {
    // 1) Handle JWT from URL on first render
    if (!hasHandledJwt && jwt_from_url) {
      console.log('[LinkBigCommerceAccount] JWT found in URL. Clearing stale state and attempting auto-login.');
      
      // ============================================
      // CRITICAL: Clear stale session state before new auth
      // This prevents "one site per account" conflicts from 
      // leftover state from previous test sessions
      // ============================================
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('jwtToken');
      sessionStorage.removeItem('site_id');
      sessionStorage.removeItem('site_token');
      sessionStorage.removeItem('shop_domain');
      sessionStorage.removeItem('store_hash');
      sessionStorage.removeItem('pendingShopifySite');
      
      // Clear any platform-specific setup flags
      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && (key.startsWith('shop_') || key.startsWith('bc_') || key.startsWith('bb_') || key.startsWith('baobrain_'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => sessionStorage.removeItem(key));
      
      login(jwt_from_url);
      
      // Remove 'jwt' param from URL
      const newParams = new URLSearchParams(location.search);
      newParams.delete('jwt');
      navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
      setHasHandledJwt(true);
      return; // Stop this effect run to let the next one handle the new token
    }

    // After handling the JWT from URL, or if no JWT was present in URL initially:
    // 2) If they hit this route without a valid JWT, send them to login
    if (!token || tokenIsExpired(token)) {
      console.log('[LinkBigCommerceAccount] No valid JWT detected or it\'s expired AFTER JWT param check. Redirecting to login for authentication.');
      setToken(null);
      sessionStorage.removeItem('jwtToken');
      // stitch the platform-specific params into the login URL
      const current = location.pathname + location.search;
      const platformParams = `&platform=${platform}&site_id=${site_id}`;
      navigate(
        `/login?return_to=${encodeURIComponent(current)}${platformParams}`,
        { replace: true }
      );
      return;
    }

    // 3) Now ensure we have site_id + site_token. If not, it's an invalid state for claiming.
    if (!site_id || !site_token) {
      setError(`Missing ${platform} site parameters (site_id or site_token). Please reinstall the app from the ${platform} Marketplace.`);
      return;
    }

    // 4) POST to /api/sites/claim
    async function claim() {
      try {
        const res = await fetch(`${API}/api/sites/claim`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ site_id: Number(site_id), site_token }),
        });
        
        if (res.status === 401) {
          console.warn(`[LinkAccount] 401 Unauthorized during /api/sites/claim for ${platform}. Clearing token and redirecting to login.`);
          navigate(`/login?return_to=${encodeURIComponent(currentPathAndQuery)}&platform=${platform}&site_id=${site_id}`, { replace: true });
          return;
        }

        if (res.status === 409) { // Conflict: site already claimed by another user
          console.warn(`[LinkAccount] Site already claimed by another user for ${platform}.`);
          setClaimConflict(true);
          setError(`This ${platform} store has already been linked by another user. Please log in with the correct account or contact support.`);
          return; // Stop further processing and show conflict UI
        } else if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.detail || res.statusText || `Failed to claim ${platform} site.`);
        }
        
        console.log(`[LinkAccount] ${platform} site claimed/verified. Redirecting to Connect${platform} page.`);
        // Determine the next page based on platform. If it's BigCommerce, use the existing path,
        // otherwise, you might have a different component name or dashboard.
        const connectPath = platform === 'BigCommerce' ? `/connect-bigcommerce` : `/connect-${platform.toLowerCase()}`;

        navigate(
          // Example: /connect-bigcommerce or /connect-shopify
          connectPath +
          `?site_id=${encodeURIComponent(site_id)}` + 
          `&site_token=${encodeURIComponent(site_token)}` + 
          `&store_hash=${encodeURIComponent(store_hash)}` +
          `&platform=${platform}`
        );
      } catch (e) {
        console.error("Error claiming BigCommerce site:", e);
        setError(e.message);
        setClaimConflict(false); // Ensure conflict state is off for generic errors
      }
    }

    // Once we’re logged in (either via URL-param or an existing session), always claim
    // but only if we haven’t already errored out or redirected, and haven't claimed yet.
    if (!hasClaimed && token && !tokenIsExpired(token) && !claimConflict) {
      setHasClaimed(true); // Set hasClaimed to true to prevent future calls
      claim();
    }
  }, [token, site_id, site_token, store_hash, platform, location, navigate, setToken, login, jwt_from_url, hasHandledJwt, hasClaimed, claimConflict, currentPathAndQuery]);

  // Display conflict UI if a claim conflict occurred
  if (claimConflict) {
    return (
      <Card sectioned>
        <Banner status="critical" title={`This ${platform} store is already linked`}>
          <p>{error}</p>
          <BlockStack gap="200">
            <Button primary onClick={() => navigate('/login')}>Login with another account</Button>
            <Button onClick={() => navigate('/support')}>Contact Support</Button>
          </BlockStack>
        </Banner>
      </Card>
    );
  }
  
  // Display generic error banner if an error occurred (not a conflict)
  if (error) {
    return <Banner status="critical">{error}</Banner>;
  }

  // Display a loading spinner while the claiming process is underway
  return (
    <Card sectioned>
      <BlockStack alignment="center">
        <Spinner size="large" accessibilityLabel={`Linking your ${platform} store…`} />
        <Text as="p">Linking your {platform} store, please wait…</Text>
      </BlockStack>
    </Card>
  );
}